import { CallSplitRounded, InfoOutlined, Inventory2Outlined } from '@mui/icons-material';
import { Box, Chip, IconButton, Tooltip } from '@mui/material';
import { FC, memo, MouseEvent, useMemo } from 'react';
import ReactGA from 'react-ga4';
import { quantityValueText, volumeValueText, weightValueText } from 'src/components/dispatch/metricText';
import {
  activeInfoMetrics, DisplayMetric, infoMetricLabel, resolvePlaceMetrics,
} from 'src/components/dispatch/unassignedDisplay/displayMetricKeys';
import { EMPTY_INFO_VALUE, InfoMetricList } from 'src/components/dispatch/unassignedDisplay/InfoMetricList';
import { SameLocationTag } from 'src/components/dispatch/unassignedDisplay/SameLocationTag';
import { SameLocationInfo } from 'src/components/dispatch/unassignedDisplay/sameLocationVM';
import { SUB_TEXT_COLOR, SUB_TEXT_WEIGHT, SUPPLEMENT_TEXT_COLOR } from 'src/components/dispatch/unassignedDisplay/unassignedTextColors';
import { UnassignedItem } from 'src/entities/dispatch';
import { PlanningsDeliveryEntity } from 'src/entities/PlanningsDelivery.entity';
import { PlanningsDriverEntity } from 'src/entities/PlanningsDriver.entity';
import { PlanningsNotAllocReason } from 'src/entities/PlanningsNotAllocReasons.entity';
import { PlanningsTruckEntity } from 'src/entities/PlanningsTruck.entity';
import { getInfoMetricValue } from 'src/utils/metricHelpers';

import { NotAllocReasonPresenter } from '../V3Plans/presenters/NotAllocReasonPresenter';

/**
 * 未割当カードの 積/降 の 1 行。ラベルは小さく灰色にして、地点名だけが読めるようにする
 * （サンプル lovable のカードと同じ構え）。
 */
/** 'YYYY-MM-DD' → 'M/D'。Date を作らずに切り出す（ブラウザ TZ で日がずれないように） */
const monthDayOf = (day: string | null | undefined): string | null => {
  const matched = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day ?? '');
  return matched ? `${Number(matched[2])}/${Number(matched[3])}` : null;
};

const PlaceRow: FC<{ label: '積' | '降'; value: string; date?: string | null }> = ({
  label, value, date,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0 }}>
    <Box component="span" sx={{ flexShrink: 0, fontSize: 9, color: SUPPLEMENT_TEXT_COLOR }}>
      {label}
    </Box>
    {date && (
      <Box
        component="span"
        sx={{
          flexShrink: 0,
          fontSize: 11,
          fontVariantNumeric: 'tabular-nums',
          color: SUPPLEMENT_TEXT_COLOR,
        }}
      >
        {date}
      </Box>
    )}
    <Box
      component="span"
      sx={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontSize: 12,
        fontWeight: 500,
        letterSpacing: '-0.01em',
        color: 'hsl(var(--card-foreground))',
      }}
    >
      {value}
    </Box>
  </Box>
);

export type UnassignedCardContentProps = {
  item: UnassignedItem;
  displayMetrics: Set<DisplayMetric>;
  /** GA の screen_name。/map と convenience で異なるため呼び出し側から渡す */
  screenName: string;
  /** 未指定なら詳細ボタンを描かない（/map は詳細パネル本体が P3 のため渡さない） */
  onShowDetail?: (item: UnassignedItem) => void;
  notAllocReasons?: PlanningsNotAllocReason[];
  notAllocDeliveries?: PlanningsDeliveryEntity[];
  notAllocTrucks?: PlanningsTruckEntity[];
  notAllocDrivers?: PlanningsDriverEntity[];
  onRestoreSplit?: (item: UnassignedItem) => void;
  /** 指定時は「分割を戻す」ボタンを disabled にし、素の title 属性で理由を出す（Task 10 #3）。 */
  restoreSplitDisabledReason?: string;
  /** 会社設定の任意項目名。任意項目キーの列挙に使う */
  customInputFieldNames: string[];
  /**
   * 積地・降地の行に積日・降日を添える。複数日を 1 画面に出す長距離配車だけ true。
   *
   * Why not 常に出す: `/map` とカード配車は 1 日を開く画面で、並んでいる案件が
   * そもそも同じ日なので、全行に同じ日付が並ぶだけになる。
   */
  showPlaceDates?: boolean;
  /** 同一地点がすでに他車両へ割当済みなら渡る (log_web_issues#318)。未指定ならタグを描かない */
  sameLocation?: SameLocationInfo;
  /**
   * タグから回転行を選択する。行を押したときは 1 件、バッジを押したときは全件
   * (log_web_issues#323)。未指定なら行を押せなくする。
   */
  onSelectVehicleRows?: (rowIds: readonly string[]) => void;
};

/**
 * DraggableCardItem の中身（useDraggable と style の組み立てを除く全部）。
 * selected はここに渡さない（外側 Box の選択枠にしか使わないため。渡すと選択が
 * 変わるたびに中身まで再描画され、memo 境界を分けた意味が無くなる）。
 * readOnly も同じ理由で渡さない（中身のどの分岐にも使っていない。disabled/attributes/
 * カーソルの sx はすべてラッパー側にしか無い）。
 */
export const UnassignedCardContent: FC<UnassignedCardContentProps> = memo(({
  item,
  displayMetrics,
  screenName,
  onShowDetail,
  notAllocReasons,
  notAllocDeliveries,
  notAllocTrucks,
  notAllocDrivers,
  onRestoreSplit,
  restoreSplitDisabledReason,
  customInputFieldNames,
  showPlaceDates = false,
  sameLocation,
  onSelectVehicleRows,
}) => {
  const hasReasons = (notAllocReasons?.length ?? 0) > 0;

  const metrics: string[] = [];
  if (displayMetrics.has('quantity')) {
    metrics.push(item.cases != null ? `${quantityValueText(item.cases)} cs` : '-- cs');
  }
  if (displayMetrics.has('weight')) {
    // legRun の実値があればそのまま表示 (0 含む)。null なら "--"。
    metrics.push(item.weight != null ? `${weightValueText(item.weight)}kg` : '--kg');
  }
  if (displayMetrics.has('volume')) {
    metrics.push(item.volume != null ? `${volumeValueText(item.volume)}m³` : '--m³');
  }

  // 積地・降地はカード本体に 積/降 のラベル付きで出すので、補足行からは外す
  // （同じ文字が 2 か所に並ぶと、チェックを切り替えても変化が読み取れない）
  const places = useMemo(() => resolvePlaceMetrics([...displayMetrics]), [displayMetrics]);

  // 関数名 activeInfoMetrics（displayMetricKeys の import）と衝突するのでローカル変数は
  // cardInfoMetrics に改名する。
  const cardInfoMetrics = useMemo(
    () => activeInfoMetrics([...displayMetrics], customInputFieldNames)
      .filter((m) => m !== 'origin' && m !== 'destination'),
    [displayMetrics, customInputFieldNames],
  );

  const infoEntries = useMemo(
    () => cardInfoMetrics.map((metric) => ({
      key: metric,
      label: infoMetricLabel(metric),
      value: getInfoMetricValue(metric, item) || EMPTY_INFO_VALUE,
    })),
    [item, cardInfoMetrics],
  );

  return (
    <>
      {item.needsReviewCourseCodes != null && item.needsReviewCourseCodes.length > 0 && (
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          label={`要確認 ${item.needsReviewCourseCodes.join(' / ')}`}
          sx={{ mb: 0.5 }}
        />
      )}
      {/* 同一地点の割当済みタグは未割当理由より上。どちらも「この案件をどう扱うか」の
          手がかりだが、割当済みは他車両との重複を避けるための情報で先に読ませたい
          （サンプル logposeTech/route-planner の UnassignedPanel と同じ並び）。 */}
      {sameLocation && (
        <SameLocationTag info={sameLocation} onSelectVehicles={onSelectVehicleRows} />
      )}
      {/* 未割当理由はカードの一番上に独立した行で出す。ヘッダ行に混ぜると
          降地名称と幅を奪い合い、どちらも省略記号に埋もれる。
          NotAllocReasonPresenter は Popover を持つので、ドラッグと干渉しないよう
          pointerDown を止める wrapper で囲む。 */}
      {hasReasons && (
        <Box
          onPointerDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          sx={{ mb: 0.5 }}
        >
          <NotAllocReasonPresenter
            variant="badge"
            filteredNotAllocReasons={notAllocReasons ?? []}
            notAllocDeliveries={notAllocDeliveries ?? []}
            notAllocTrucks={notAllocTrucks ?? []}
            notAllocDrivers={notAllocDrivers ?? []}
          />
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Inventory2Outlined sx={{ fontSize: 14, mt: 0.25, flexShrink: 0, color: 'text.secondary' }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {/* 積/降 の見出し行。表示項目のチェックで出し分ける（両方外したら降地が残る） */}
          {places.origin && (
            <PlaceRow
              label="積"
              value={item.origin ?? ''}
              date={showPlaceDates ? monthDayOf(item.loadingDate) : null}
            />
          )}
          {places.destination && (
            <PlaceRow
              label="降"
              value={item.destination || item.storeName}
              date={showPlaceDates ? monthDayOf(item.unloadingDate) : null}
            />
          )}
          <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', columnGap: 1, fontSize: '11px', fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', color: SUB_TEXT_COLOR, fontWeight: SUB_TEXT_WEIGHT }}>
            {metrics.map((m, i) => (
              <span key={i}>{m}</span>
            ))}
          </Box>
          <InfoMetricList entries={infoEntries} />
        </Box>
        {item.wasSplit && onRestoreSplit && (
          <Box
            onPointerDown={(e) => e.stopPropagation()}
            sx={{ flexShrink: 0 }}
          >
            {/* 理由は Tooltip に載せ、span で包む。直下の子に素の title を付けると MUI が
                警告を出し、disabled な要素は hover を拾わないので Tooltip も出ない。
                describeChild は読み上げ名をボタンの aria-label に保つため */}
            <Tooltip
              title={restoreSplitDisabledReason || '分割された案件です（クリックで分割を戻す）'}
              arrow
              describeChild
            >
              <span>
              <IconButton
                size="small"
                disabled={!!restoreSplitDisabledReason}
                onClick={(e) => {
                  e.stopPropagation();
                  ReactGA.event('click', {
                    screen_name: screenName,
                    button_name: '未割当案件 分割を戻す',
                  });
                  onRestoreSplit?.(item);
                }}
                aria-label="分割を戻す"
                sx={{ p: 0.25 }}
              >
                <CallSplitRounded sx={{ fontSize: 14, color: 'error.main' }} />
              </IconButton>
              </span>
            </Tooltip>
          </Box>
        )}
        {/* 案件詳細を開く info ボタン (サンプル準拠)。pointerDown を止めてドラッグと干渉させない。
            未指定なら描かない (P3-b で /map からも渡すようになったが、この契約自体は不変) */}
        {onShowDetail && (
          <Box
            onPointerDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            sx={{ flexShrink: 0 }}
          >
            <IconButton
              size="small"
              aria-label="詳細を表示"
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                ReactGA.event('click', {
                  screen_name: screenName,
                  button_name: '未割当案件 詳細表示',
                });
                onShowDetail(item);
              }}
              sx={{
                p: 0.25,
                color: 'hsl(var(--muted-foreground) / 0.5)',
                '&:hover': { color: 'hsl(var(--foreground))', bgcolor: 'hsl(var(--secondary))' },
              }}
            >
              <InfoOutlined sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        )}
      </Box>
    </>
  );
});

UnassignedCardContent.displayName = 'UnassignedCardContent';
